/**
 * AASB 16 — Leases (lessee accounting) rule pack v1.0.0
 *
 * Validates a portfolio of leases on the standard schema (lease_id, commencement_date,
 * term, payment, frequency, discount rate, ROU asset, liability) by recomputing the
 * IFRS 16 schedule and comparing reported balances at the as-at date.
 *
 * Out of scope (flagged where present, not recalculated):
 *  - Lease modifications / reassessments
 *  - Variable lease payments
 *  - Subleases
 *  - Lessor accounting (this rulepack is lessee-only)
 *  - Sale-and-leaseback
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';

const RULEPACK_VERSION = 'aasb16-1.0.0';

type CellValue = string | number | boolean | null;

interface LeaseRow {
  lease_id: string;
  lessor: string;
  asset_type: string;
  commencement_date: Date;
  lease_term_months: number;
  lease_payment: number;
  payment_frequency: 'monthly' | 'quarterly' | 'annually';
  payment_timing: 'start_of_period' | 'end_of_period';
  discount_rate: number;        // annual decimal, e.g. 0.05 for 5%
  right_of_use_asset: number;   // reported as-at balance
  lease_liability: number;      // reported as-at balance
  as_at_date: Date;
  short_term_flag?: boolean;
  low_value_flag?: boolean;
}

const REQUIRED: (keyof LeaseRow)[] = [
  'lease_id', 'lessor', 'commencement_date', 'lease_term_months',
  'lease_payment', 'payment_frequency', 'discount_rate',
  'right_of_use_asset', 'lease_liability',
];

// --- header mapping ---
const HEADER_MAP: Record<string, string> = {};
function reg(canonical: string, ...syns: string[]) {
  HEADER_MAP[canonical.toLowerCase()] = canonical;
  for (const s of syns) HEADER_MAP[s.toLowerCase()] = canonical;
}
reg('lease_id', 'lease id', 'lease no', 'lease number');
reg('lessor', 'landlord', 'lease counterparty');
reg('asset_type', 'asset type', 'asset class', 'underlying asset');
reg('commencement_date', 'commencement date', 'start date', 'lease start', 'effective date');
reg('lease_term_months', 'lease term (months)', 'lease term', 'term months', 'term');
reg('lease_payment', 'lease payment', 'periodic payment', 'rental', 'payment');
reg('payment_frequency', 'payment frequency', 'frequency', 'payment cycle');
reg('payment_timing', 'payment timing', 'timing');
reg('discount_rate', 'discount rate', 'incremental borrowing rate', 'ibr', 'rate');
reg('right_of_use_asset', 'right of use asset', 'right-of-use asset', 'rou asset', 'rou');
reg('lease_liability', 'lease liability', 'liability');
reg('as_at_date', 'as at', 'as at date', 'reporting date');
reg('short_term_flag', 'short term', 'short term flag', 'short-term');
reg('low_value_flag', 'low value', 'low value flag', 'low-value');

function normaliseHeader(h: string): string | null {
  return HEADER_MAP[String(h ?? '').trim().toLowerCase()] ?? null;
}

function parseNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/[$,£€% ]/g, '').trim();
  const neg = /^\(.*\)$/.test(s);
  const clean = neg ? s.slice(1, -1) : s;
  const n = Number(clean);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

function parsePercent(v: unknown): number | null {
  const n = parseNumber(v);
  if (n == null) return null;
  // Accept either 0.05 or 5 (treat anything > 1 as a percentage point form)
  return n > 1 ? n / 100 : n;
}

function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(+v) ? null : v;
  if (typeof v === 'number') {
    const ms = (v - 25569) * 86400 * 1000;
    const d = new Date(ms);
    return isNaN(+d) ? null : d;
  }
  const s = String(v).trim();
  const au = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (au) {
    const [, dd, mm, yyyy] = au;
    const year = yyyy.length === 2 ? 2000 + Number(yyyy) : Number(yyyy);
    const d = new Date(year, Number(mm) - 1, Number(dd));
    return isNaN(+d) ? null : d;
  }
  const iso = new Date(s);
  return isNaN(+iso) ? null : iso;
}

function normaliseFrequency(v: unknown): LeaseRow['payment_frequency'] | null {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.startsWith('month') || s === 'm') return 'monthly';
  if (s.startsWith('quart') || s === 'q') return 'quarterly';
  if (s.startsWith('annual') || s === 'yearly' || s === 'year' || s === 'y' || s === 'a') return 'annually';
  return null;
}

function normaliseTiming(v: unknown): LeaseRow['payment_timing'] {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.startsWith('start') || s === 'advance' || s === 'arrears_no' || s === 'beginning' || s === 'begin') return 'start_of_period';
  return 'end_of_period';
}

function periodsPerYear(freq: LeaseRow['payment_frequency']): number {
  return freq === 'monthly' ? 12 : freq === 'quarterly' ? 4 : 1;
}

function monthsPerPeriod(freq: LeaseRow['payment_frequency']): number {
  return freq === 'monthly' ? 1 : freq === 'quarterly' ? 3 : 12;
}

function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) +
    (b.getDate() >= a.getDate() ? 0 : -1);
}

/**
 * Present value of an annuity at the lease commencement.
 * payment is per period; rate is per-period rate (annual / periodsPerYear); n is number of periods.
 * timing 'start_of_period' = annuity due (× (1+r)).
 */
function presentValue(payment: number, perPeriodRate: number, periods: number, timing: LeaseRow['payment_timing']): number {
  if (perPeriodRate === 0) return payment * periods * (timing === 'start_of_period' ? 1 : 1);
  const factor = (1 - Math.pow(1 + perPeriodRate, -periods)) / perPeriodRate;
  return payment * factor * (timing === 'start_of_period' ? (1 + perPeriodRate) : 1);
}

interface SchedulePoint {
  periodIndex: number;          // 0 = at commencement
  monthsElapsed: number;
  liability: number;
  rou: number;
  interest: number;             // for the period ending here
  principal: number;
  payment: number;
}

function buildSchedule(r: LeaseRow): { schedule: SchedulePoint[]; openingLiability: number; openingROU: number; perPeriodRate: number } {
  const ppy = periodsPerYear(r.payment_frequency);
  const perPeriodRate = r.discount_rate / ppy;
  const totalPeriods = Math.round(r.lease_term_months / monthsPerPeriod(r.payment_frequency));
  const openingLiability = presentValue(r.lease_payment, perPeriodRate, totalPeriods, r.payment_timing);
  // Initial direct costs / incentives assumed zero unless explicitly modelled
  const openingROU = openingLiability;

  const schedule: SchedulePoint[] = [];
  schedule.push({
    periodIndex: 0, monthsElapsed: 0,
    liability: openingLiability, rou: openingROU,
    interest: 0, principal: 0, payment: 0,
  });

  let liability = openingLiability;
  for (let p = 1; p <= totalPeriods; p++) {
    let interest: number;
    let principal: number;
    if (r.payment_timing === 'start_of_period') {
      // Payment at start: principal first, then interest accrues on remaining balance
      principal = r.lease_payment;
      liability = liability - principal;
      interest = liability * perPeriodRate;
      liability = liability + interest;
    } else {
      // Payment at end: interest accrues then payment reduces balance
      interest = liability * perPeriodRate;
      principal = r.lease_payment - interest;
      liability = liability + interest - r.lease_payment;
    }
    if (liability < 0 && Math.abs(liability) < 0.01) liability = 0;
    schedule.push({
      periodIndex: p,
      monthsElapsed: p * monthsPerPeriod(r.payment_frequency),
      liability,
      rou: openingROU * (1 - p / totalPeriods),
      interest,
      principal,
      payment: r.lease_payment,
    });
  }
  return { schedule, openingLiability, openingROU, perPeriodRate };
}

function ulid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function mkFinding(ruleId: string, severity: Finding['severity'], title: string, message: string, opts: Partial<Pick<Finding, 'rowId' | 'evidence' | 'suggestedAdjustment'>> = {}): Finding {
  return {
    id: ulid(),
    ruleId,
    standard: 'AASB_16',
    severity,
    title,
    message,
    evidence: opts.evidence ?? {},
    suggestedAdjustment: opts.suggestedAdjustment,
    rowId: opts.rowId,
    rulepackVersion: RULEPACK_VERSION,
  };
}

function acceptedHeadersFor(canonical: string): string[] {
  return Object.entries(HEADER_MAP).filter(([, c]) => c === canonical).map(([k]) => k);
}

export function detectAASB16(headerRow: string[]): { confidence: number; matched: string[]; missing: string[] } {
  const hits = new Set<string>();
  for (const raw of headerRow) {
    const c = normaliseHeader(String(raw ?? ''));
    if (c) hits.add(c);
  }
  const matched = REQUIRED.filter(r => hits.has(r as string));
  const missing = REQUIRED.filter(r => !hits.has(r as string));
  return { confidence: matched.length / REQUIRED.length, matched: matched as string[], missing: missing as string[] };
}

export function validateAASB16(
  headerRow: string[],
  dataRows: CellValue[][],
  defaultAsAtDate: Date = new Date()
): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];

  const idx: Partial<Record<keyof LeaseRow, number>> = {};
  headerRow.forEach((raw, i) => {
    const c = normaliseHeader(String(raw ?? ''));
    if (c) (idx as any)[c] = i;
  });

  for (const f of REQUIRED) {
    if (idx[f] === undefined) {
      schemaErrors.push(`Missing required column "${f}". Accepted: ${acceptedHeadersFor(f as string).join(', ')}.`);
    }
  }
  if (schemaErrors.length) {
    return {
      summary: {
        standard: 'AASB_16', rulepackVersion: RULEPACK_VERSION,
        rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 },
        runAt: new Date().toISOString(),
      },
      findings, schemaErrors, disclaimer: DISCLAIMER,
    };
  }

  const rows: LeaseRow[] = [];
  dataRows.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: keyof LeaseRow) => raw[idx[k] as number];
    const id = String(get('lease_id') ?? `row-${rowNum}`);

    // R01 completeness
    const missing = REQUIRED.filter(f => {
      const v = get(f);
      return v == null || v === '';
    });
    if (missing.length) {
      findings.push(mkFinding('AASB16.R01', 'error', 'Required field missing',
        `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`,
        { rowId: id, evidence: { missing } }));
      return;
    }

    const term = parseNumber(get('lease_term_months'));
    const payment = parseNumber(get('lease_payment'));
    const rate = parsePercent(get('discount_rate'));
    const rou = parseNumber(get('right_of_use_asset'));
    const liab = parseNumber(get('lease_liability'));
    const freq = normaliseFrequency(get('payment_frequency'));
    const start = parseDate(get('commencement_date'));
    const timing = normaliseTiming(get('payment_timing'));
    const asAt = idx.as_at_date !== undefined ? (parseDate(get('as_at_date')) ?? defaultAsAtDate) : defaultAsAtDate;

    if (!(term !== null && term > 0)) {
      findings.push(mkFinding('AASB16.R02', 'error', 'Lease term must be positive', `Row ${rowNum} (${id}): term ${term}.`, { rowId: id }));
      return;
    }
    if (!(payment !== null && payment > 0)) {
      findings.push(mkFinding('AASB16.R04', 'error', 'Lease payment must be positive', `Row ${rowNum} (${id}): payment ${payment}.`, { rowId: id }));
      return;
    }
    if (!(rate !== null && rate > 0)) {
      findings.push(mkFinding('AASB16.R03', 'warning', 'Discount rate must be positive', `Row ${rowNum} (${id}): rate ${rate}. Recalculation skipped.`, { rowId: id }));
      return;
    }
    if (!freq) {
      findings.push(mkFinding('AASB16.R03', 'warning', 'Unknown payment frequency', `Row ${rowNum} (${id}): frequency not recognised. Use monthly / quarterly / annually.`, { rowId: id }));
      return;
    }
    if (start && start > asAt) {
      findings.push(mkFinding('AASB16.R05', 'error', 'Commencement date in future', `Row ${rowNum} (${id}): ${start.toISOString().slice(0, 10)} > as-at ${asAt.toISOString().slice(0, 10)}.`, { rowId: id }));
      return;
    }

    // R06 short-term lease
    const shortTerm = Boolean(get('short_term_flag'));
    if (shortTerm || term <= 12) {
      findings.push(mkFinding('AASB16.R06', 'info', 'Short-term lease — exempt from on-balance recognition',
        `Row ${rowNum} (${id}): term ${term} months ≤ 12. Lessee may apply the short-term exemption (AASB 16.5(a)). Recognise as expense on a straight-line or other systematic basis. Recalculation skipped.`,
        { rowId: id, evidence: { term } }));
      return;
    }

    // R07 low-value lease
    if (Boolean(get('low_value_flag'))) {
      findings.push(mkFinding('AASB16.R07', 'info', 'Low-value lease — exempt',
        `Row ${rowNum} (${id}): low-value flag set. AASB 16.5(b) exemption applies; recognise as expense.`,
        { rowId: id }));
      return;
    }

    rows.push({
      lease_id: id,
      lessor: String(get('lessor') ?? ''),
      asset_type: String(get('asset_type') ?? ''),
      commencement_date: start ?? asAt,
      lease_term_months: term,
      lease_payment: payment,
      payment_frequency: freq,
      payment_timing: timing,
      discount_rate: rate,
      right_of_use_asset: rou!,
      lease_liability: liab!,
      as_at_date: asAt,
    });
  });

  // Per-lease deep checks
  for (const r of rows) {
    const { schedule, openingLiability, openingROU, perPeriodRate } = buildSchedule(r);
    const monthsElapsed = Math.max(0, Math.min(monthsBetween(r.commencement_date, r.as_at_date), r.lease_term_months));
    // Find the closest schedule point to monthsElapsed
    const point = schedule.reduce((best, p) => Math.abs(p.monthsElapsed - monthsElapsed) < Math.abs(best.monthsElapsed - monthsElapsed) ? p : best, schedule[0]);
    const tolLiab = Math.max(50, 0.005 * openingLiability);
    const tolROU = Math.max(50, 0.005 * openingROU);

    // R09 — lease liability recalculation
    const expectedLiab = point.liability;
    const liabDelta = r.lease_liability - expectedLiab;
    if (Math.abs(liabDelta) > tolLiab) {
      const sev: Finding['severity'] = Math.abs(liabDelta) > 2 * tolLiab ? 'error' : 'warning';
      findings.push(mkFinding('AASB16.R09', sev, 'Lease liability differs from recalculated PV schedule',
        `Lease ${r.lease_id}: reported liability $${fmt(r.lease_liability)}, expected $${fmt(expectedLiab)} (delta ${liabDelta >= 0 ? '+' : '−'}$${fmt(Math.abs(liabDelta))}, tolerance ±$${fmt(tolLiab)}).`,
        {
          rowId: r.lease_id,
          evidence: { reported: r.lease_liability, expected: expectedLiab, delta: liabDelta, monthsElapsed, term: r.lease_term_months, perPeriodRate, timing: r.payment_timing },
          suggestedAdjustment: { field: 'lease_liability', from: r.lease_liability, to: +expectedLiab.toFixed(2), rationale: `PV schedule, ${monthsElapsed}/${r.lease_term_months} months elapsed.` },
        }));
    }

    // R10 — ROU asset (straight-line amortisation in this rulepack)
    const expectedROU = openingROU * (1 - monthsElapsed / r.lease_term_months);
    const rouDelta = r.right_of_use_asset - expectedROU;
    if (Math.abs(rouDelta) > tolROU) {
      const sev: Finding['severity'] = Math.abs(rouDelta) > 2 * tolROU ? 'error' : 'warning';
      findings.push(mkFinding('AASB16.R10', sev, 'Right-of-use asset differs from straight-line amortisation',
        `Lease ${r.lease_id}: reported ROU $${fmt(r.right_of_use_asset)}, expected $${fmt(expectedROU)} (delta ${rouDelta >= 0 ? '+' : '−'}$${fmt(Math.abs(rouDelta))}, tolerance ±$${fmt(tolROU)}).`,
        {
          rowId: r.lease_id,
          evidence: { reported: r.right_of_use_asset, expected: expectedROU, delta: rouDelta, monthsElapsed, term: r.lease_term_months },
          suggestedAdjustment: { field: 'right_of_use_asset', from: r.right_of_use_asset, to: +expectedROU.toFixed(2), rationale: `Straight-line over lease term, ${monthsElapsed}/${r.lease_term_months}.` },
        }));
    }

    // R11 — negative liability or ROU
    if (r.lease_liability < 0) {
      findings.push(mkFinding('AASB16.R11', 'error', 'Negative lease liability', `Lease ${r.lease_id}: liability $${fmt(r.lease_liability)} cannot be negative.`, { rowId: r.lease_id }));
    }
    if (r.right_of_use_asset < 0) {
      findings.push(mkFinding('AASB16.R11', 'error', 'Negative ROU asset', `Lease ${r.lease_id}: ROU $${fmt(r.right_of_use_asset)} cannot be negative.`, { rowId: r.lease_id }));
    }

    // R12 — fully amortised
    if (monthsElapsed >= r.lease_term_months) {
      if (Math.abs(r.lease_liability) > tolLiab) {
        findings.push(mkFinding('AASB16.R12', 'warning', 'Lease term expired but liability not fully extinguished', `Lease ${r.lease_id}: term elapsed; reported liability $${fmt(r.lease_liability)} > 0.`, { rowId: r.lease_id }));
      }
      if (Math.abs(r.right_of_use_asset) > tolROU) {
        findings.push(mkFinding('AASB16.R12', 'warning', 'Lease term expired but ROU not fully amortised', `Lease ${r.lease_id}: term elapsed; reported ROU $${fmt(r.right_of_use_asset)} > 0.`, { rowId: r.lease_id }));
      }
    }

    // R13 — extreme rate sanity
    if (r.discount_rate > 0.20) {
      findings.push(mkFinding('AASB16.R13', 'info', 'Discount rate appears unusually high',
        `Lease ${r.lease_id}: discount rate ${(r.discount_rate * 100).toFixed(2)}% — confirm this is the lessee's incremental borrowing rate, not a credit spread.`,
        { rowId: r.lease_id, evidence: { rate: r.discount_rate } }));
    }
    if (r.discount_rate < 0.01) {
      findings.push(mkFinding('AASB16.R13', 'info', 'Discount rate appears unusually low',
        `Lease ${r.lease_id}: discount rate ${(r.discount_rate * 100).toFixed(2)}% — confirm this is the rate implicit in the lease or the lessee's IBR.`,
        { rowId: r.lease_id, evidence: { rate: r.discount_rate } }));
    }
  }

  const compliantSet = new Set(rows.map(r => r.lease_id));
  for (const f of findings) {
    if ((f.severity === 'error' || f.severity === 'warning') && f.rowId) compliantSet.delete(f.rowId);
  }

  return {
    summary: {
      standard: 'AASB_16',
      rulepackVersion: RULEPACK_VERSION,
      rowsTested: rows.length,
      compliant: compliantSet.size,
      byseverity: {
        info: findings.filter(f => f.severity === 'info').length,
        warning: findings.filter(f => f.severity === 'warning').length,
        error: findings.filter(f => f.severity === 'error').length,
      },
      runAt: new Date().toISOString(),
    },
    findings,
    disclaimer: DISCLAIMER,
  };
}
